import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, ButtonGroup } from '@chakra-ui/react';
import Chart from 'chart.js/auto';
import 'chartjs-adapter-date-fns';

const StockPriceChart = ({ priceHistory }) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const [selectedRange, setSelectedRange] = useState('1Y');

  const ranges = {
    // '1D': 1,
    '1W': 7,
    '1M': 30,
    '3M': 90,
    '6M': 180,
    '1Y': 365,
    '5Y': 1825,
  };

  const filterDataByRange = (range) => {
    const now = new Date();

    const filteredData = priceHistory
      .filter(data => {
        const date = new Date(data.date);
        return (now - date) / (1000 * 60 * 60 * 24) <= ranges[range];
      });
    return filteredData;
  };

  useEffect(() => {
    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
    }

    const filteredData = filterDataByRange(selectedRange);

    const priceData = Object.keys(filteredData).map(timestamp => filteredData[timestamp].close);
    const volumeData = Object.keys(filteredData).map(timestamp => filteredData[timestamp].volume);

    const ctx = chartRef.current.getContext('2d');
    chartInstanceRef.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: Object.keys(filteredData).map(timestamp => new Date(filteredData[timestamp].date)),
        datasets: [
          {
            label: 'Prezzo Azione',
            data: priceData,
            fill: false,
            backgroundColor: 'rgba(75,192,192,0.4)',
            borderColor: 'rgba(75,192,192,1)',
            tension: 0.1,
            pointRadius: 0, // Eliminate dots in the plot
            hoverRadius: 5, // Highlight the point when hovering
            yAxisID: 'y',
          },
          {
            label: 'Volume',
            data: volumeData,
            fill: true,
            backgroundColor: 'rgba(253, 226, 73, 0.4)',
            borderColor: 'rgba(253, 226, 73, 1)',
            tension: 0.1,
            pointRadius: 0,
            hoverRadius: 5,
            yAxisID: 'y_volume',
          }
        ],
      },
      options: {
        interaction: {
          mode: 'index',
          intersect: false,
        },
        hover: {
          mode: 'index',
          intersect: false,
        },
        scales: {
          x: {
            type: 'time',
            time: {
              unit: 'day',
              tooltipFormat: 'dd-MM-yyyy', // Format for tooltip
              displayFormats: {
                day: 'dd-MM-yyyy', // Format for x-axis labels
              },
            },
            title: {
              display: true,
              text: 'Data',
            },
          },
          y: {
            title: {
              display: true,
              text: 'Prezzo',
            },
          },
          y_volume: {
            title: {
              display: true,
              text: 'Volume',
            },
            display: true,
            position: 'right',
            // grid line settings
            grid: {
              display: false,
            },
            max: Math.max(...volumeData) * 3, // change the scale of the volume axis to make the volume smaller compare with price
          },
        },
      },
    });

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, [priceHistory, selectedRange]);

  return (
    <Box width="100%" height="400px">
      <ButtonGroup size="sm" isAttached variant="outline" mb={4}>
        {Object.keys(ranges).map(range => (
          <Button
            key={range}
            onClick={() => setSelectedRange(range)}
            isActive={selectedRange === range}
          >
            {range}
          </Button>
        ))}
      </ButtonGroup>
      <canvas ref={chartRef}></canvas>
    </Box>
  );
};

export default StockPriceChart;